import { ChangeEvent, useEffect, useState } from "react";
import datePickerStyles from "../../styles/UI/date.module.css";
import IconButtonComponent from "../UI/buttons/IconButton";
import { ReactComponent as BlackArrowRight } from "../../assets/icons/chevron_right_black.svg";
import dateUtil from "../../utils/dateUtil";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { clearKpiData } from "../../store/KPICollection";
import { openDragAndDropSessionWarningModal } from "../../store/dragAndDrop";
import RequestCancellationService from "../../service/requestCancellationService";

//TODO - Tanja update date to match URL on landing.
const DatePicker = ({
  min,
  max,
  showArrows,
  setSelectedDate,
  bgColor,
  color,
  defaultDate,
  cleanUpFn,
}: {
  min?: string;
  max?: string;
  showArrows?: boolean;
  setSelectedDate: (date: string) => void;
  bgColor?: string;
  color?: string;
  defaultDate?: string;
  cleanUpFn?: () => void;
}) => {
  const dispatch = useAppDispatch();
  const { selectedGanttChartTab } = useAppSelector(
    (state) => state.ganttChartSlice
  );

  const [guiDateValue, setGuiDateValue] = useState<string | undefined>();

  const sanitizeDateInput = (value?: string | Date) => {
    if (!value) {
      return dateUtil.getCalendarValueString(new Date());
    }

    if (value instanceof Date) {
      return dateUtil.getCalendarValueString(value);
    }

    const trimmedValue = value.trim();

    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedValue)) {
      return trimmedValue;
    }

    const parsedDate = new Date(trimmedValue);

    if (Number.isNaN(parsedDate.getTime())) {
      return dateUtil.getCalendarValueString(new Date());
    }

    return dateUtil.getCalendarValueString(parsedDate);
  };

  // Format: YYYY-MM-DD
  const [inputDateValue, setInputDateValue] = useState<string>(
    sanitizeDateInput(defaultDate)
  );

  useEffect(() => {
    const nextInputValue = sanitizeDateInput(defaultDate);

    setInputDateValue((prev: string) => {
      if (prev === nextInputValue) {
        return prev;
      }
      return nextInputValue;
    });
  }, [defaultDate]);

  useEffect(() => {
    setSelectedDate(new Date(inputDateValue).toISOString());
    setGuiDateValue(dateUtil.getCalendarDate(new Date(inputDateValue)));
  }, [inputDateValue]);

  const moveXDay = (numberOfMoves: number) => {
    // Perform any necessary clean-up when changing dates
    cleanUpFn?.();
    // Do the actual work
    const dayBeforeCurrentlySelectedDay = new Date(inputDateValue);
    dayBeforeCurrentlySelectedDay.setDate(
      dayBeforeCurrentlySelectedDay.getDate() + numberOfMoves
    );
    setInputDateValue(
      dateUtil.getCalendarValueString(dayBeforeCurrentlySelectedDay)
    );
  };

  const handleArrowClick = (count: number) => {
    if (selectedGanttChartTab === "Custom")
      dispatch(openDragAndDropSessionWarningModal()); // Disallow datechange when in edit mode
    else {
      // Perform cleanup
      dispatch(clearKpiData());
      moveXDay(count);
      cancelRunningAPICalls();
    }
  };

  const cancelRunningAPICalls = () => {
    // Cancel all active ganttChartData requests before starting a new batch
    const requestService = RequestCancellationService.getInstance();
    requestService.cancelAllRequests();
  };

  return (
    <div className="flex jutify-center">
      {showArrows && (
        <IconButtonComponent
          key="moveBackwardIcon"
          id="moveBackwardIcon"
          icon={<BlackArrowRight style={{ transform: "rotate(180deg)" }} />}
          disable={inputDateValue === min}
          onClick={() => {
            handleArrowClick(-1);
          }}
        />
      )}
      <div className={datePickerStyles.datepickerWrapper}>
        <input
          style={{ backgroundColor: bgColor ? bgColor : "white" }}
          min={min}
          max={max}
          type="date"
          value={inputDateValue}
          onClick={(event) => {
            if (selectedGanttChartTab === "Custom") {
              event.preventDefault(); // Make sure calendar stays closed when in Edit mode
              event.stopPropagation();
              dispatch(openDragAndDropSessionWarningModal());
            }
          }}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            // Clean up any KpiData
            dispatch(clearKpiData());
            setInputDateValue(sanitizeDateInput(event.target.value));

            cancelRunningAPICalls();
          }}
        />
        <p
          className={datePickerStyles.datepickerSubstitute}
          style={{
            backgroundColor: bgColor ? bgColor : "white",
            color: color ? color : "white",
          }}
        >
          {guiDateValue}
        </p>
      </div>
      {showArrows && (
        <IconButtonComponent
          key="moveForwardIcon"
          id="moveForwardIcon"
          icon={<BlackArrowRight />}
          disable={inputDateValue === max}
          onClick={() => {
            handleArrowClick(1);
          }}
        />
      )}
    </div>
  );
};
export default DatePicker;
